import { useEffect, useMemo, useRef, useState } from 'react';

import { PDFViewer } from '@embedpdf/react-pdf-viewer';
import { Linkedin01Icon, Mail01Icon, ShieldEnergyIcon } from '@hugeicons/core-free-icons';
import cn from 'classnames';
import { shouldHideCustomReferenceAiSummary } from 'helpers/reference-ai-summary';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';

import { FormMessage, MessageType } from 'components/FormComponents';
import { Button } from 'components/FormComponents/Button/Button';

import { selectCompanyChecks } from 'api/company-checks-slice';
import { useGetReportPdfDataUrlQuery } from 'api/referees-api';
import { useGetRefereeResponseQuery } from 'api/referees-api';

import { RefereeMetadataDetailsModal } from '../RefereeMetadataDetailsModal/RefereeMetadataDetailsModal';
import { Statuses, VerificationCard } from './../VerificationCard/VerificationCard';
import styles from './RefereeReport.module.scss';

export const RefereeReport = ({
  candidate,
  referee,
  referenceCheck,
  brand = {},
  setLoading = () => {},
}) => {
  const companyChecks = useSelector(selectCompanyChecks);

  const { t } = useTranslation();
  const { data: pdfUrl, isLoading, isFetching, isError } = useGetReportPdfDataUrlQuery(referee.id);

  const [emailVerification, setEmailVerification] = useState({ status: '', msg: '' });
  const [linkedinVerification, setLinkedinVerification] = useState({ status: '', msg: '' });
  const [ipVerification, setIpVerification] = useState({ status: '', msg: '' });
  const [showMetadataDetails, setShowMetadataDetails] = useState(false);
  const embedPdfContainerRef = useRef(null);

  useEffect(() => {
    setLoading(isLoading || isFetching);
  }, [isLoading, isFetching]);

  const handlePdfViewerReady = registry => {
    const container = embedPdfContainerRef.current;
    if (!container?.shadowRoot || container.shadowRoot.querySelector('style[data-toolbar-scale]'))
      return;
    // Only scales the toolbar (not the rendered page images), since the toolbar's rem-based
    // sizing shrinks against this app's 10px root font-size vs the 16px it assumes. Targets
    // EmbedPDF's own data-epdf-i hook rather than its generated classes.
    const style = document.createElement('style');
    style.setAttribute('data-toolbar-scale', '');
    style.textContent = '[data-epdf-i="main-toolbar"] { zoom: 1.6; }';
    container.shadowRoot.appendChild(style);

    // Matches the redaction's own black mark color, since the library's default red/orange
    // stroke reads as a distinct highlight rather than a plain pending redaction mark.
    registry.getPlugin('annotation')?.provides()?.setToolDefaults('redact', {
      strokeColor: '#000000',
    });
  };

  // Set verification statuses
  useEffect(() => {
    // Email
    if (referee?.email === candidate?.email)
      setEmailVerification({ status: Statuses.Error, msg: 'Email addresses match' });
    else if (referee?.has_company_email)
      setEmailVerification({ status: Statuses.Success, msg: 'Registered Company Email' });
    else setEmailVerification({ status: Statuses.Warn, msg: 'Personal email used' });

    // Linkedin
    if (referee?.linkedin_profile)
      setLinkedinVerification({ status: Statuses.Success, msg: 'Verified Linkedin' });
    else setLinkedinVerification({ status: Statuses.Warn, msg: 'No information provided' });

    // IP
    if (referee?.ip === candidate?.ip)
      setIpVerification({ status: Statuses.Error, msg: 'IP address matches candidate' });
    else setIpVerification({ status: Statuses.Success, msg: 'Unique IP Address' });
  }, [candidate, referee]);

  return (
    <div>
      <div className="flex flex-col px-8 pt-8 pb-0 gap-8 bg-slate-50">
        {ipVerification.status === Statuses.Error && (
          <FormMessage
            closeable={false}
            noMargin
            type={MessageType.Error}
            message="IP address matches candidate. This may indicate the reference was completed on the same device or network."
          />
        )}
        <div className="flex items-stretch w-full gap-8 ">
          <VerificationCard>
            <VerificationCard.Header icon={Mail01Icon} hugeicons>
              email address
            </VerificationCard.Header>
            <VerificationCard.Link href={`mailto:${referee.email}`}>
              {referee.email}
            </VerificationCard.Link>
            <VerificationCard.AlertMessage status={emailVerification.status}>
              {emailVerification.msg}
            </VerificationCard.AlertMessage>
          </VerificationCard>
          <VerificationCard>
            <VerificationCard.Header icon={Linkedin01Icon} hugeicons>
              linkedin profile
            </VerificationCard.Header>
            <VerificationCard.Value>
              {referee.linkedin_profile ? referee.linkedin_profile : 'N/A'}
            </VerificationCard.Value>
            <VerificationCard.AlertMessage status={linkedinVerification.status}>
              {linkedinVerification.msg}
            </VerificationCard.AlertMessage>
          </VerificationCard>
          <VerificationCard>
            <VerificationCard.Header icon={ShieldEnergyIcon} hugeicons>
              ip address
            </VerificationCard.Header>
            <VerificationCard.Value>{referee.ip}</VerificationCard.Value>
            <VerificationCard.AlertMessage status={ipVerification.status}>
              {ipVerification.msg}
            </VerificationCard.AlertMessage>
            {referenceCheck.ip && (
              <VerificationCard.Value>
                <Button
                  type="link"
                  className="u-font-size-x-small"
                  onClick={() => setShowMetadataDetails(true)}>
                  View Metadata
                </Button>
              </VerificationCard.Value>
            )}
          </VerificationCard>
        </div>
      </div>

      <div className="border-b border-neutral-4 p-8 bg-neutral-2">
        {pdfUrl && !isError && (
          <PDFViewer
            key={pdfUrl}
            className={cn(
              styles.pdfViewer,
              'rounded-3xl border border-neutral-4 overflow-hidden text-[24px]',
            )}
            config={{
              src: pdfUrl,
              tabBar: 'never',
              disabledCategories: [
                'document-open',
                'document-close',
                'panel-comment',
                'mode-annotate',
                'mode-shapes',
              ],
              zoom: { defaultZoomLevel: 'fit-width' },
              ui: { stylesheetConfig: { useContainerQueries: false } },
            }}
            onInit={container => {
              embedPdfContainerRef.current = container;
            }}
            onReady={handlePdfViewerReady}
          />
        )}
        {isError && (
          <FormMessage
            closeable={false}
            noMargin
            type={MessageType.Error}
            message={t('check.refereeReport.unavailable')}
          />
        )}
      </div>

      <RefereeMetadataDetailsModal
        candidate={candidate}
        referee={referee}
        referenceCheck={referenceCheck}
        visible={showMetadataDetails}
        onClose={() => setShowMetadataDetails(false)}
      />
    </div>
  );
};
